import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { BilingualText, ExportFormat, RecommendationRow, Tier } from 'shared';
import { api } from '../api';
import { Note } from './Note';
import { DISPLAY, MONO, tokens } from '../theme';

/**
 * Danh sách đề xuất chỉnh sửa — spec SC-02 §5.
 *
 * Bước này trước đây bị khoá với lý do "đang phát triển"; giờ mở ra ở mức
 * ĐỀ XUẤT, không phải "áp dụng": Copilot không ghi vào mã nguồn của ai, nên
 * nhãn bước cũng bỏ chữ "áp dụng" để không hứa thứ không có.
 */

const TIER_BG: Record<Tier, string> = { A: tokens.mint, B: tokens.amber, C: tokens.rose };
const TIER_FG: Record<Tier, string> = { A: tokens.ink, B: '#43301A', C: '#fff' };
const TIER_HINT: Record<Tier, string> = {
  A: 'Sửa được ngay, ít rủi ro',
  B: 'Nên có người rà lại trước khi đăng',
  C: 'Bắt buộc người có thẩm quyền duyệt',
};

const PRIORITY_LABEL = { high: 'ưu tiên cao', medium: 'ưu tiên vừa', low: 'ưu tiên thấp' };

function isBilingual(v: unknown): v is BilingualText {
  return typeof v === 'object' && v !== null && 'vi' in v && 'en' in v;
}

/** Đoạn dán được, dạng chuỗi phẳng. Nội dung song ngữ ghép thành cặp data-vi/data-en. */
function copyText(proposed: RecommendationRow['proposed']): string | null {
  if (proposed === null) return null;
  if (isBilingual(proposed)) return `${proposed.vi}\n${proposed.en}`;
  return proposed;
}

/**
 * Nút sao chép có phản hồi thấy được.
 *
 * Đổi nhãn thành "Đã sao chép" trong 2 giây: không có phản hồi thì người dùng
 * bấm ba bốn lần vì không biết đã ăn chưa (spec SC-02 §3.4).
 */
function CopyButton({ text }: { text: string }) {
  const [xong, setXong] = useState(false);

  async function sao_chep() {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Trình duyệt cũ hoặc trang không chạy trên https thì không có clipboard
      // API. Không làm gì thêm — báo lỗi ở đây gây ồn hơn giá trị nó mang lại.
      return;
    }
    setXong(true);
    setTimeout(() => setXong(false), 2000);
  }

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
      onClick={() => void sao_chep()}
    >
      {xong ? 'Đã sao chép' : 'Sao chép'}
    </Button>
  );
}

/** Khối so sánh trước/sau: dòng `-` nền đỏ nhạt, dòng `+` nền xanh nhạt. */
function DiffBlock({ diff }: { diff: RecommendationRow['diff'] }) {
  if (diff.length === 0) return null;
  return (
    <Box
      sx={{
        mt: 1.4,
        border: `1px solid ${tokens.line2}`,
        borderRadius: '8px',
        overflow: 'hidden',
        fontFamily: MONO,
        fontSize: 12.5,
      }}
    >
      {diff.map((d, i) => (
        <Box
          key={i}
          sx={{
            px: 1.4,
            py: 0.5,
            bgcolor: d.op === '-' ? tokens.roseBg : tokens.okBg,
            color: d.op === '-' ? tokens.roseText : tokens.okText,
            // Đoạn mã dài phải xuống dòng chứ không đẩy cả thẻ rộng ra.
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {d.op} {d.text}
        </Box>
      ))}
    </Box>
  );
}

function RecommendationCard({
  item,
  checked,
  onToggle,
}: {
  item: RecommendationRow;
  checked: boolean;
  onToggle: () => void;
}) {
  const text = copyText(item.proposed);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.6 }}>
      <Stack direction="row" spacing={1.2} alignItems="flex-start">
        <Checkbox
          size="small"
          checked={checked}
          onChange={onToggle}
          sx={{ mt: '-6px', ml: '-6px' }}
          inputProps={{ 'aria-label': `Chọn đề xuất ${item.title}` }}
        />
        <Box minWidth={0} flex={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={item.tier}
              title={TIER_HINT[item.tier]}
              sx={{
                bgcolor: TIER_BG[item.tier],
                color: TIER_FG[item.tier],
                fontWeight: 700,
                height: 20,
              }}
            />
            <Typography sx={{ fontFamily: DISPLAY, fontSize: 15, fontWeight: 600, color: tokens.ink }}>
              {item.title}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
              {PRIORITY_LABEL[item.priority]}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.2 }}>
            {item.subtitle}
          </Typography>

          <Box sx={{ mt: 1.4 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '.1em', color: tokens.faint, fontWeight: 600 }}>
              VẤN ĐỀ
            </Typography>
            <Typography sx={{ fontSize: 13.5 }}>{item.problem}</Typography>
          </Box>
          <Box sx={{ mt: 1.1 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '.1em', color: tokens.faint, fontWeight: 600 }}>
              VÌ SAO
            </Typography>
            <Typography sx={{ fontSize: 13.5 }}>{item.why}</Typography>
          </Box>

          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1.1 }}>
            Vị trí: {item.location}
          </Typography>

          <DiffBlock diff={item.diff} />

          <Stack direction="row" spacing={1} sx={{ mt: 1.4 }} alignItems="center">
            {/* Không có nội dung dán được thì ẨN HẲN nút, không hiện nút mờ:
                nút bấm vào không ra gì làm người dùng tưởng hệ thống hỏng. */}
            {text ? (
              <CopyButton text={text} />
            ) : (
              // Câu này cố ý KHÔNG đoán lý do. Ba nhóm mục rơi vào đây vì ba lý
              // do khác nhau — cần người viết nội dung, cần AI, hoặc chưa kiểm
              // được qua HTTP — và phần "Vấn đề"/"Vì sao" ngay trên đã nói rõ
              // từng trường hợp. Nói gộp thành "cần người viết nội dung" là sai
              // với hai nhóm còn lại.
              <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                Mục này chưa có nội dung dán sẵn — xem phần “Vấn đề” và “Vì sao” ở trên.
              </Typography>
            )}
            {item.tier === 'C' && (
              <Typography sx={{ fontSize: 12.5, color: tokens.roseText }}>
                Cần người có thẩm quyền duyệt
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export function RecommendationPanel({ slug }: { slug: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['recommendations', slug],
    queryFn: () => api.recommendations(slug),
  });

  const [bo_chon, setBoChon] = useState<Set<string>>(new Set());

  // Mặc định TICK HẾT: người dùng vào đây để lấy cả gói mang đi, bỏ bớt vài mục
  // dễ hơn là tự tay tick từng mục trong hai chục cái.
  const items = useMemo(() => data?.items ?? [], [data]);
  const da_chon = items.filter((i) => !bo_chon.has(i.id));

  // Hai đường tải dùng CHUNG một mutation, phân biệt bằng tham số: dùng hai
  // mutation riêng thì hai nút quay vòng chờ độc lập, và bấm nút này trong lúc
  // nút kia đang chạy sẽ tải về hai file cùng lúc.
  const tai = useMutation({
    mutationFn: (format: ExportFormat) =>
      api.exportRecommendations(slug, da_chon.map((i) => i.id), format),
  });

  if (isLoading) return <Skeleton variant="rounded" height={260} />;
  if (error) return <Note tone="bad">{(error as Error).message}</Note>;
  if (!data || items.length === 0) {
    return (
      <Note>
        {data?.scanId
          ? 'Lần quét gần nhất không tìm thấy vấn đề nào cần sửa. Không có gì để đề xuất.'
          : 'Trang này chưa được quét lần nào. Bấm “Quét lại” để có đề xuất.'}
      </Note>
    );
  }

  const dem = (t: Tier) => items.filter((i) => i.tier === t).length;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 600, color: tokens.ink }}>
            {items.length} đề xuất chỉnh sửa
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {(['A', 'B', 'C'] as const)
              .filter((t) => dem(t) > 0)
              .map((t) => `${dem(t)} Tier ${t}`)
              .join(' · ')}
          </Typography>
        </Box>
        {/* Ba nút trên một hàng là chạm ngưỡng của màn hẹp — cho xuống dòng thay vì
            để nút cuối bị đẩy ra ngoài mép thẻ. */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            color="inherit"
            onClick={() =>
              setBoChon(bo_chon.size === 0 ? new Set(items.map((i) => i.id)) : new Set())
            }
          >
            {bo_chon.size === 0 ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
          </Button>
          {/* Prompt là nút đầy màu vì đó là đường đi nhanh hơn hẳn: giao file cho
              AI có mã nguồn thì nó sửa luôn, còn bản hướng dẫn vẫn phải dán tay
              từng mục. Ai muốn tự dán vẫn có nút bên cạnh. */}
          <Button
            variant="outlined"
            startIcon={
              tai.isPending && tai.variables === 'huong-dan' ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={da_chon.length === 0 || tai.isPending}
            onClick={() => tai.mutate('huong-dan')}
          >
            Tải hướng dẫn sửa
          </Button>
          <Button
            variant="contained"
            startIcon={
              tai.isPending && tai.variables === 'prompt' ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SmartToyIcon />
              )
            }
            disabled={da_chon.length === 0 || tai.isPending}
            onClick={() => tai.mutate('prompt')}
          >
            Tải prompt cho AI ({da_chon.length})
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <Note>
          <strong>Prompt cho AI</strong> là file <code>.md</code> đưa cho Claude Code / Cursor —
          công cụ đó có mã nguồn nên sửa được luôn, kèm sẵn luật cấm bịa số liệu và mục nào phải
          chờ duyệt. <strong>Hướng dẫn sửa</strong> là bản để bạn tự dán tay. Cả hai đều không
          phải patch áp bằng <code>git apply</code> — Copilot đọc trang qua HTTP nên không có mã
          nguồn của bạn.
        </Note>
      </Box>

      {data.renderWarning && (
        <Box sx={{ mb: 2 }}>
          <Note tone="warn">
            Lần quét này nghi trang dựng bằng JavaScript, nên các đề xuất về nội dung (độ dài, FAQ,
            liên kết nội bộ) đã bị bỏ qua — dữ liệu đọc được không đủ tin để khuyên gì.
          </Note>
        </Box>
      )}

      {tai.error && (
        <Box sx={{ mb: 2 }}>
          <Note tone="bad">{(tai.error as Error).message}</Note>
        </Box>
      )}

      {items.map((item) => (
        <RecommendationCard
          key={item.id}
          item={item}
          checked={!bo_chon.has(item.id)}
          onToggle={() =>
            setBoChon((cu) => {
              const moi = new Set(cu);
              if (moi.has(item.id)) moi.delete(item.id);
              else moi.add(item.id);
              return moi;
            })
          }
        />
      ))}
    </Box>
  );
}
